import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { set, get } from 'idb-keyval';
import { toast } from 'react-toastify';
import * as actions from '../../../redux/actions/growthEngine';
import { getDestinationUrlError, normalizeDestinationUrl } from './DestinationUrlStep';

const EMPTY_FACEBOOK_MEDIA = { photos: [], videos: [] };

const normalizeFacebookMedia = (data) => {
    if (!data || Array.isArray(data)) {
        return EMPTY_FACEBOOK_MEDIA;
    }
    return {
        photos: Array.isArray(data.photos) ? data.photos : [],
        videos: Array.isArray(data.videos) ? data.videos : [],
    };
};

const isMetaMediaEmpty = (facebookData, instagramData) => {
    const facebook = normalizeFacebookMedia(facebookData);
    return (
        facebook.photos.length === 0 &&
        facebook.videos.length === 0 &&
        (!Array.isArray(instagramData) || instagramData.length === 0)
    );
};

export const StepFive = () => {    const INITIAL_MEDIA_BATCH = 12;
    const dispatch = useDispatch();
    const { formData, goalOptions } = useSelector(state => state.growthEngine);
    // Get selected goal's CTA options
    const selectedGoal = goalOptions.find(goal => goal.value === formData.objective);
    const ctaOptions = selectedGoal ? selectedGoal.ctaOptions : [];
    const [uploadedMedia, setUploadedMedia] = useState([]);
    const [isFmPageLoading, setFmLoading] = useState(false);
    const [metaMediaLoading, setMetaMediaLoading] = useState(false);
    const [instagramPaging, setInstagramPaging] = useState({ after: null, hasMore: false, loading: false });
    const [facebookPaging, setFacebookPaging] = useState({
        photosAfter: null,
        videosAfter: null,
        hasMore: false,
        loading: false,
    });
    const [urlTouched, setUrlTouched] = useState(false);
    const mediaAccountKey = `${formData.pageId || ""}:${formData.instagramId || ""}`;
    const fetchedAccountKeyRef = useRef("");
    const instagramMediaRef = useRef(formData.instagramMediaData || []);
    const facebookMediaRef = useRef(normalizeFacebookMedia(formData.facebookMediaData));
    const destinationUrlError = getDestinationUrlError(formData.destinationUrl);
    const showUrlError = formData.destinationUrl?.trim() || urlTouched ? destinationUrlError : "";
    const isYoutubeDestinationLocked = formData.objective === "YouTube Growth";

    const facebookPosts = useMemo(() => {
        const videos = (formData.facebookMediaData?.videos || []).map((post) => ({
            ...post,
            type: "VIDEO",
            platform: "Facebook",
        }));
        const photos = (formData.facebookMediaData?.photos || []).map((post) => ({
            ...post,
            type: "IMAGE",
            platform: "Facebook",
        }));
        return [...videos, ...photos];
    }, [formData.facebookMediaData]);

    useEffect(() => {
        instagramMediaRef.current = formData.instagramMediaData || [];
    }, [formData.instagramMediaData]);

    useEffect(() => {
        facebookMediaRef.current = normalizeFacebookMedia(formData.facebookMediaData);
    }, [formData.facebookMediaData]);

    useEffect(() => {
        const fetchMetaAssets = async () => {
            if (!formData.pageId || !formData.instagramId) return;

            const accountChanged = fetchedAccountKeyRef.current !== mediaAccountKey;
            if (!accountChanged && !isMetaMediaEmpty(formData.facebookMediaData, formData.instagramMediaData)) {
                return;
            }

            if (accountChanged) {
                dispatch(actions.updateFormData({
                    facebookMediaData: EMPTY_FACEBOOK_MEDIA,
                    instagramMediaData: [],
                }));
                setInstagramPaging({ after: null, hasMore: false, loading: false });
                setFacebookPaging({
                    photosAfter: null,
                    videosAfter: null,
                    hasMore: false,
                    loading: false,
                });
            }

            const data = {
                page_id: formData.pageId,
                instagram_id: formData.instagramId,
                limit: INITIAL_MEDIA_BATCH,
                section: "all",
            };

            const response = await dispatch(actions.getMetaAccountAssets(data, toast, setMetaMediaLoading));
            if (response?.status === 200) {
                fetchedAccountKeyRef.current = mediaAccountKey;
                dispatch(actions.updateFormData({
                    facebookMediaData: normalizeFacebookMedia(response?.data?.facebook),
                    instagramMediaData: response?.data?.instagram || [],
                }));
                setInstagramPaging({
                    after: response?.paging?.instagram?.after || null,
                    hasMore: Boolean(response?.paging?.instagram?.hasMore),
                    loading: false,
                });
                setFacebookPaging({
                    photosAfter: response?.paging?.facebook?.photosAfter || null,
                    videosAfter: response?.paging?.facebook?.videosAfter || null,
                    hasMore: Boolean(response?.paging?.facebook?.hasMore),
                    loading: false,
                });
            }
        };

        fetchMetaAssets();
    }, [formData.pageId, formData.instagramId, mediaAccountKey, dispatch]);

    useEffect(() => {
        const storedMedia = JSON.parse(localStorage.getItem("uploadedMedia")) || [];
        setUploadedMedia(storedMedia);
    }, []);

    const mergeUniqueById = (existing, incoming) => {
        const seen = new Set(existing.map((item) => item.id));
        const uniqueIncoming = incoming.filter((item) => !seen.has(item.id));
        return [...existing, ...uniqueIncoming];
    };

    const loadMoreSection = useCallback(async (section) => {
        if (section === "instagram") {
            if (instagramPaging.loading || !instagramPaging.hasMore) return;
            setInstagramPaging((prev) => ({ ...prev, loading: true }));
            const response = await dispatch(actions.getMetaAccountAssets({
                page_id: formData.pageId,
                instagram_id: formData.instagramId,
                limit: INITIAL_MEDIA_BATCH,
                section: "instagram",
                instagram_after: instagramPaging.after,
            }, toast));

            if (response?.status === 200) {
                dispatch(actions.updateFormData({
                    instagramMediaData: mergeUniqueById(
                        instagramMediaRef.current || [],
                        response?.data?.instagram || []
                    )
                }));
                setInstagramPaging({
                    after: response?.paging?.instagram?.after || null,
                    hasMore: Boolean(response?.paging?.instagram?.hasMore),
                    loading: false,
                });
            } else {
                setInstagramPaging((prev) => ({ ...prev, loading: false }));
            }
            return;
        }

        if (facebookPaging.loading || !facebookPaging.hasMore) return;
        setFacebookPaging((prev) => ({ ...prev, loading: true }));
        const currentFacebookMedia = facebookMediaRef.current;
        const response = await dispatch(actions.getMetaAccountAssets({
            page_id: formData.pageId,
            instagram_id: formData.instagramId,
            limit: INITIAL_MEDIA_BATCH,
            section: "facebook",
            facebook_photos_after: facebookPaging.photosAfter,
            facebook_videos_after: facebookPaging.videosAfter,
        }, toast));

        if (response?.status === 200) {
            const nextFacebookMedia = normalizeFacebookMedia(response?.data?.facebook);
            dispatch(actions.updateFormData({
                facebookMediaData: {
                    photos: mergeUniqueById(
                        currentFacebookMedia.photos || [],
                        nextFacebookMedia.photos || []
                    ),
                    videos: mergeUniqueById(
                        currentFacebookMedia.videos || [],
                        nextFacebookMedia.videos || []
                    ),
                }
            }));
            setFacebookPaging({
                photosAfter: response?.paging?.facebook?.photosAfter || null,
                videosAfter: response?.paging?.facebook?.videosAfter || null,
                hasMore: Boolean(response?.paging?.facebook?.hasMore),
                loading: false,
            });
        } else {
            setFacebookPaging((prev) => ({ ...prev, loading: false }));
        }
    }, [
        dispatch,
        facebookPaging.hasMore,
        facebookPaging.loading,
        facebookPaging.photosAfter,
        facebookPaging.videosAfter,
        formData.instagramId,
        formData.pageId,
        instagramPaging.after,
        instagramPaging.hasMore,
        instagramPaging.loading,
    ]);

    const handleSectionScroll = (event, section) => {
        const target = event.currentTarget;
        const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (distanceFromBottom > 80) return;
        loadMoreSection(section);
    };

    const attachLoadMoreObserver = useCallback((node, section, hasMore, loading) => {
        if (!node || !hasMore || loading) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    loadMoreSection(section);
                }
            },
            { root: node.parentElement, rootMargin: "120px", threshold: 0.1 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [loadMoreSection]);

    const instagramSentinelRef = useRef(null);
    const facebookSentinelRef = useRef(null);

    useEffect(() => {
        const node = instagramSentinelRef.current;
        if (!node || metaMediaLoading) return undefined;
        return attachLoadMoreObserver(node, "instagram", instagramPaging.hasMore, instagramPaging.loading);
    }, [
        attachLoadMoreObserver,
        instagramPaging.hasMore,
        instagramPaging.loading,
        metaMediaLoading,
        formData.instagramMediaData?.length,
    ]);

    useEffect(() => {
        const node = facebookSentinelRef.current;
        if (!node || metaMediaLoading) return undefined;
        return attachLoadMoreObserver(node, "facebook", facebookPaging.hasMore, facebookPaging.loading);
    }, [
        attachLoadMoreObserver,
        facebookPaging.hasMore,
        facebookPaging.loading,
        metaMediaLoading,
        facebookPosts.length,
    ]);

    const normalizeSelectedPost = (post) => {
        const videoUrl = post.url || post.source || post.media_url || null;
        return {
            ...post,
            url: videoUrl || post.url || null,
            source: post.source || videoUrl || null,
            thumbnail_url: post.thumbnail_url || null,
        };
    };

    const handlePostSelect = (post) => {
        const normalizedPost = normalizeSelectedPost(post);
        // Check if the post is already selected
        const isAlreadySelected = formData.selectedPost.some(item => item.id === normalizedPost.id);

        const updatedPosts = isAlreadySelected
            ? formData.selectedPost.filter(item => item.id !== normalizedPost.id) // Remove if exists
            : [...formData.selectedPost, normalizedPost]; // Add if not exists

        // If trying to add more than 5
        if (!isAlreadySelected && formData.selectedPost.length >= 5) {
            toast.warning("You can not select more than 5 posts.", {
                position: "top-center",
                theme: "colored",
                hideProgressBar: true,
            });
            return;
        }

        // Reset uploaded media when selecting posts
        localStorage.removeItem("uploadedMedia");
        setUploadedMedia([]);
        // Determine new caption: Use first post's caption if posts exist, else empty
        const updatedCaption = updatedPosts.length > 0 ? updatedPosts[0].caption || "" : "";
        // If there are no selected posts left, reset selectMediaBy to an empty string
        const updatedSelectMediaBy = updatedPosts.length === 0 ? "" : "meta";

        dispatch(actions.updateFormData({
            selectedPost: updatedPosts,
            selectMediaBy: updatedSelectMediaBy,
            adCaption: updatedCaption
        }));
    };

    // Handle CTA change
    const handleCtaChange = (e) => {
        dispatch(actions.updateFormData({ callToAction: e.target.value }));
    };


    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        const existingMeta = JSON.parse(localStorage.getItem("uploadedMedia") || "[]");
        if (existingMeta.length + files.length > 5) {
            toast.warning("You can not upload more than 5 media files.", {
                position: "top-center",
                theme: "colored",
                hideProgressBar: true,
            });
            return;
        }

        const newMetaFiles = files.map((file, i) => ({
            id: `${Date.now()}-${i}`,
            name: file.name,
            type: file.type.includes("video") ? "video" : "image",
            previewUrl: URL.createObjectURL(file)
        }));

        // Update localStorage
        const updatedMeta = [...existingMeta, ...newMetaFiles];
        localStorage.setItem("uploadedMedia", JSON.stringify(updatedMeta));

        // Update IndexedDB
        const existingFiles = await get("uploadedFiles") || [];
        const updatedFiles = [...existingFiles, ...files];
        await set("uploadedFiles", updatedFiles);

        setUploadedMedia(updatedMeta);
        // Optionally still update Redux if needed for UI state
        if (formData.selectMediaBy !== "upload") {
            dispatch(actions.updateFormData({
                selectMediaBy: "upload"
            }));
        }
    };

    const handleRemoveMedia = async (index) => {
        // Remove preview data from localStorage
        const storedMeta = JSON.parse(localStorage.getItem("uploadedMedia") || "[]");
        storedMeta.splice(index, 1);
        localStorage.setItem("uploadedMedia", JSON.stringify(storedMeta));
        // Remove actual file from IndexedDB
        const storedFiles = await get("uploadedFiles") || [];
        storedFiles.splice(index, 1);
        await set("uploadedFiles", storedFiles);
        setUploadedMedia(storedMeta);
        // Optionally reset selection method if no media left
        if (storedMeta.length === 0) {
            dispatch(actions.updateFormData({ selectMediaBy: "" }));
        }
    };

    return (
        <div className="font-inter text-theme_clr1 mb-[61px]" data-tour="ad-creative">
            <div className="heading text-white font-medium text-center mb-[28.5px]">
                <h5 className="text-[22.5px] mb-1">Design your ads</h5>
                <div className="description text-white text-opacity-80 text-[15px]">
                    Choose your ad creative to get started. You can select an image from your Facebook account or upload images from your device.
                </div>
            </div>
            <div className="info-box bg-white rounded-xl p-4 lg:p-5">
                <div className="flex flex-col lg:flex-row gap-4 xl:gap-5" data-tour="choose-image">
                    <div className="left-info lg:w-[50%]">
                        <div className="title-name text-[22.69px] font-medium mb-2">Choose your image</div>
                        <div className="description font-medium text-theme_clr1 text-[15px] leading-tight">
                            Choose your ad creative to get started. You can select
                            an image from your Facebook account or upload images
                            from your device.
                        </div>
                        <div className="text-red-500 text-sm font-medium mt-3">
                            ⚠ You can not select or upload more than 5 posts.
                        </div>
                        <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 shadow-sm">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center mt-0.5">
                                    <i className="bi bi-exclamation-triangle-fill text-white text-sm"></i>
                                </div>
                                <div>
                                    <div className="text-amber-700 font-bold text-sm mb-1">
                                        <i className="bi bi-music-note-beamed mr-1"></i>
                                        Instagram Music Alert
                                    </div>
                                    <p className="text-[13px] leading-snug text-amber-900/80 font-medium">
                                        Reels with songs from the Instagram Music library can't be used as ad creatives — Meta treats them as licensed audio, even if it's your own song.
                                    </p>
                                    <div className="mt-2 flex flex-col gap-1">
                                        <div className="flex items-start gap-1.5 text-[12px] text-amber-800 font-medium">
                                            <i className="bi bi-check-circle-fill text-green-600 mt-0.5"></i>
                                            Upload the same video with audio already embedded
                                        </div>
                                        <div className="flex items-start gap-1.5 text-[12px] text-amber-800 font-medium">
                                            <i className="bi bi-check-circle-fill text-green-600 mt-0.5"></i>
                                            Post Reels without selecting from Instagram Music library
                                        </div>
                                        <div className="flex items-start gap-1.5 text-[12px] text-amber-800 font-medium">
                                            <i className="bi bi-x-circle-fill text-red-500 mt-0.5"></i>
                                            Posts with Instagram Music can’t be selected and will appear greyed out.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="right-info lg:w-[50%]">
                        <div className="btn-wrapper flex flex-wrap gap-2 mb-3">
                            <button onClick={() => dispatch(actions.updateFormData({ mediaView: 'metaListing' }))}
                                className={`text-theme_clr2 text-xs font-medium border-2 border-theme_clr2 rounded-lg px-[13px] py-[11.5px] ${formData.mediaView === 'metaListing' ? 'bg-theme_clr1' : 'bg-white'}`}>Facebook/Instagram</button>
                            <button onClick={() => dispatch(actions.updateFormData({ mediaView: 'uploadSection' }))}
                                className={`text-theme_clr2 text-xs font-medium border-2 border-theme_clr2 rounded-lg px-[13px] py-[11.5px] ${formData.mediaView === 'uploadSection' ? 'bg-theme_clr1' : 'bg-white'}`}>Upload</button>
                        </div>

                        <hr className="border-1 border-slate-300 my-3" />

                        {formData.mediaView === 'metaListing' && (
                            <>
                                {/* ------------------ INSTAGRAM SECTION ------------------ */}
                                <div className="social-img-wrapper pb-2">
                                    <div className="btn-wrapper text-right leading-none mb-3">
                                        Instagram Posts
                                    </div>

                                    {/* Skeleton Loader */}
                                    {metaMediaLoading ? (
                                        <div className="grid grid-cols-3 gap-3 lg:gap-4 max-h-[408px]">
                                            {[...Array(6)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className="h-[120px] bg-gray-200 animate-pulse rounded"
                                                ></div>
                                            ))}
                                        </div>
                                    ) : formData.instagramMediaData?.length === 0 ? (
                                        <div className="text-center text-sm text-gray-500 py-4">
                                            No Instagram posts available
                                        </div>
                                    ) : (
                                        <div
                                            className="images-list grid grid-cols-3 gap-3 lg:gap-4 max-h-[408px] overflow-y-auto overflow-x-hidden"
                                            onScroll={(event) => handleSectionScroll(event, "instagram")}
                                        >
                                            {formData.instagramMediaData?.map((post, index) => {
                                                const isUnusable = post.type === "VIDEO" && !post.url && !post.media_url && !post.source;
                                                const isSelected = formData.selectedPost?.some(item => item.id === post.id);
                                                return (
                                                <div
                                                    key={index}
                                                    className={`items h-[120px] border relative rounded p-1 group ${
                                                        isUnusable
                                                            ? "cursor-not-allowed"
                                                            : "cursor-pointer"
                                                    } ${isSelected ? "selected border-2 border-theme_clr2" : ""}`}
                                                    onClick={() => {
                                                        if (isUnusable) {
                                                            toast.info(
                                                                "This Reel uses Instagram Music and can't be used as an ad creative. Upload the same video with embedded audio instead.",
                                                                { position: "top-center", theme: "colored", hideProgressBar: true }
                                                            );
                                                            return;
                                                        }
                                                        handlePostSelect({ ...post, platform: "Instagram" });
                                                    }}
                                                    title=""
                                                >
                                                    <div className={isUnusable ? "opacity-40 w-full h-full" : "w-full h-full"}>
                                                    {post.type === "VIDEO" ? (
                                                        post.url ? (
                                                            <video
                                                                src={post.url}
                                                                className="w-full h-full object-cover object-center rounded-sm"
                                                            />
                                                        ) : (
                                                            <img
                                                                src={post.thumbnail_url}
                                                                alt="Instagram Post"
                                                                className="w-full h-full object-cover object-center rounded-sm"
                                                            />
                                                        )
                                                    ) : (
                                                        <img
                                                            src={post.url}
                                                            alt="Instagram Post"
                                                            className="w-full h-full object-cover object-center rounded-sm"
                                                        />
                                                    )}
                                                    </div>
                                                    {isUnusable && (
                                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-amber-500/80 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <i className="bi bi-exclamation-triangle-fill text-white text-lg mb-1"></i>
                                                            <span className="text-white text-[10px] font-semibold text-center px-2 leading-tight">
                                                                Instagram Music — can't be used in ads
                                                            </span>
                                                        </div>
                                                    )}
                                                    {isUnusable && (
                                                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 translate-y-full z-20 w-[200px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                                            <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg shadow-lg px-3 py-2 text-[11px] font-semibold leading-snug text-center">
                                                                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-amber-500 rotate-45 rounded-sm"></div>
                                                                <i className="bi bi-music-note-beamed mr-1"></i>
                                                                This Reel uses Instagram Music and can't be used as an ad creative
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                            {instagramPaging.loading && (
                                                <div className="col-span-3 text-center text-xs text-gray-500 py-2">
                                                    Loading more Instagram posts...
                                                </div>
                                            )}
                                            <div ref={instagramSentinelRef} className="col-span-3 h-1" aria-hidden="true" />
                                            {instagramPaging.hasMore && !instagramPaging.loading && (
                                                <button
                                                    type="button"
                                                    className="col-span-3 text-center text-xs font-medium text-theme_clr2 py-2 hover:underline"
                                                    onClick={() => loadMoreSection("instagram")}
                                                >
                                                    Load more Instagram posts
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <hr className="border-1 border-slate-300 my-3" />

                                {/* ------------------ FACEBOOK SECTION ------------------ */}
                                <div className="social-img-wrapper">
                                    <div className="btn-wrapper text-right leading-none mb-3">
                                        Facebook Posts
                                    </div>

                                    {/* Skeleton Loader */}
                                    {metaMediaLoading ? (
                                        <div className="grid grid-cols-3 gap-3 lg:gap-4 max-h-[408px]">
                                            {[...Array(6)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className="h-[120px] bg-gray-200 animate-pulse rounded"
                                                ></div>
                                            ))}
                                        </div>
                                    ) : (!formData.facebookMediaData?.videos?.length &&
                                        !formData.facebookMediaData?.photos?.length) ? (
                                        <div className="text-center text-sm text-gray-500 py-4">
                                            No Facebook posts available
                                        </div>
                                    ) : (
                                        <div
                                            className="images-list grid grid-cols-3 gap-3 lg:gap-4 max-h-[408px] overflow-y-auto overflow-x-hidden"
                                            onScroll={(event) => handleSectionScroll(event, "facebook")}
                                        >
                                            {facebookPosts.map((post, index) => (
                                                <div
                                                    key={index}
                                                    className={`items h-[120px] border relative rounded cursor-pointer p-1 ${formData.selectedPost?.some(item => item.id === post.id)
                                                        ? "selected border-2 border-theme_clr2"
                                                        : ""
                                                        }`}
                                                    onClick={() => handlePostSelect(post)}
                                                >
                                                    {post.type === "VIDEO" ? (
                                                        <video
                                                            src={post.source}
                                                            className="w-full h-full object-cover object-center rounded-sm"
                                                        />
                                                    ) : (
                                                        <img
                                                            src={post.url}
                                                            alt="Facebook Post"
                                                            className="w-full h-full object-cover object-center rounded-sm"
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                            {facebookPaging.loading && (
                                                <div className="col-span-3 text-center text-xs text-gray-500 py-2">
                                                    Loading more Facebook posts...
                                                </div>
                                            )}
                                            <div ref={facebookSentinelRef} className="col-span-3 h-1" aria-hidden="true" />
                                            {facebookPaging.hasMore && !facebookPaging.loading && (
                                                <button
                                                    type="button"
                                                    className="col-span-3 text-center text-xs font-medium text-theme_clr2 py-2 hover:underline"
                                                    onClick={() => loadMoreSection("facebook")}
                                                >
                                                    Load more Facebook posts
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}


                        {formData.mediaView === 'uploadSection' && (
                            <div className="input-cover my-6">
                                <input type="file" className="w-full bg-theme_clr1 text-white text-[15px] border-2 border-theme_clr1 rounded-xl leading-tight p-[13px]" placeholder="Link to ad"
                                    multiple
                                    accept="image/*,video/*"
                                    onChange={handleFileUpload}
                                />
                                <div className="images-list grid grid-cols-3 gap-3 lg:gap-4 max-h-[408px] overflow-y-auto overflow-x-hidden mt-4">
                                    {uploadedMedia?.map((media, index) => (
                                        <div key={index} className="media-item">
                                            {media.type === "image" ? (
                                                <img src={media.previewUrl} alt="Uploaded" className="block h-[120px] w-full object-cover object-center rounded-md" />
                                            ) : (
                                                <video src={media.previewUrl} width="100" controls className="block h-[120px] w-full object-cover object-center rounded-md" />
                                            )}
                                            <button onClick={() => handleRemoveMedia(index)}>Remove</button>
                                        </div>
                                    ))}

                                </div>
                            </div>
                        )}

                    </div>
                </div>

                <hr className="border-black my-4" />

                <div className="call-to-action" data-tour="call-to-actions">
                    <div className="title-name text-[22.69px] font-medium mb-1">Call-to-Action</div>
                    <div className="description font-medium text-theme_clr1 text-[15px] leading-tight mb-3">
                        Select a call-to-action for your add - when a user click this, they will be sent to your Instagram page.
                    </div>
                    <div className="select-wrapper">
                        <select value={formData.callToAction} className="w-full border border-gray-300 rounded-md outline-none px-3 py-3" name="" id=""
                            onChange={handleCtaChange}
                        >
                            {ctaOptions.map((cta, index) => (
                                <option key={index} value={cta}>{cta}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <hr className="border-black my-6" />

                <div className="wrapper" data-tour="ad-details">
                    <div className="flex flex-col lg:flex-row gap-4 xl:gap-5 mb-4">
                        <div className="left-info lg:w-[50%]">
                            <div className="title-name text-[22.69px] font-medium mb-2">Fill in your ad details</div>
                            <div className="description font-medium text-theme_clr1 text-[15px] leading-tight">
                                Customise your ad text.
                            </div>
                        </div>

                        <div className="right-info lg:w-[50%]">
                            <form action="">
                                <div className="input-cover border-2 border-theme_clr1 rounded-xl mb-2 p-[13px] pb-1">
                                    <textarea rows={5} className="w-full min-h-[110px] block text-theme_clr1 placeholder:text-theme_clr1 text-[15px] font-medium focus:outline-none" placeholder="Primary Text (what is your ad about)"
                                        value={formData.adCaption}
                                        onChange={(e) => dispatch(actions.updateFormData({ adCaption: e.target.value }))}
                                    ></textarea>
                                    <div className="text-theme_clr1 text-xs font-medium text-right mt-1">{formData.adCaption?.length || 0}/200</div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className="flex flex-col lg:flex-row gap-4 xl:gap-5">
                        <div className="left-info lg:w-[50%]">
                        </div>

                        <div className="right-info lg:w-[50%]">
                            <form action="">
                                <div className="input-cover relative mb-2">
                                    <input type="url" className={`w-full text-theme_clr1 placeholder:text-theme_clr1 text-[15px] border-2 rounded-xl leading-tight p-[13px] pt-4 appearance-none peer ${showUrlError ? 'border-red-500' : 'border-theme_clr1'} ${isYoutubeDestinationLocked ? 'bg-slate-50' : ''}`}
                                        id="floating_outlined_link_ad"
                                        placeholder=" "
                                        value={formData.destinationUrl}
                                        readOnly={isYoutubeDestinationLocked}
                                        onChange={(e) => {
                                            if (isYoutubeDestinationLocked) return;
                                            dispatch(actions.updateFormData({ destinationUrl: e.target.value }));
                                        }}
                                        onBlur={() => {
                                            if (isYoutubeDestinationLocked) return;
                                            setUrlTouched(true);
                                            const normalized = normalizeDestinationUrl(formData.destinationUrl);
                                            if (normalized !== formData.destinationUrl) {
                                                dispatch(actions.updateFormData({ destinationUrl: normalized }));
                                            }
                                        }}
                                    />
                                    <label for="floating_outlined_link_ad" class="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1">Link to ad</label>
                                    {isYoutubeDestinationLocked ? (
                                        <div className="text-theme_clr1 text-opacity-55 text-xs font-medium mt-1">
                                            Locked to your selected YouTube destination from the previous step.
                                        </div>
                                    ) : null}
                                    {showUrlError ? (
                                        <div className="text-red-600 text-xs font-medium mt-1">
                                            {showUrlError}
                                        </div>
                                    ) : null}
                                </div>

                                <div className="input-cover relative mb-2">
                                    <input type="text" className="w-full text-theme_clr1 placeholder:text-theme_clr1 text-[15px] border-2 border-theme_clr1 rounded-xl leading-tight p-[13px] pt-4 appearance-none peer"
                                        id="floating_outlined_headline"
                                        placeholder=" "
                                        value={formData.adHeadline}
                                        onChange={(e) => dispatch(actions.updateFormData({ adHeadline: e.target.value }))}
                                    />
                                    <label for="floating_outlined_headline" class="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1">Link Headline (Grab their attention)</label>
                                </div>
                                <div className="input-cover relative">
                                    <input type="text" className="w-full text-theme_clr1 placeholder:text-theme_clr1 text-[15px] border-2 border-theme_clr1 rounded-xl leading-tight p-[13px] pt-4 appearance-none peer"
                                        id="floating_outlined_description"
                                        placeholder=""
                                        value={formData.adDescription}
                                        onChange={(e) => dispatch(actions.updateFormData({ adDescription: e.target.value }))}
                                    />
                                    <label for="floating_outlined_description" class="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1">Link Description (Why should people visit)</label>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
